import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play, PackageCheck, Truck, ShieldCheck } from "lucide-react";
import { fetchMedia } from "../lib/api";

/**
 * Full-width premium trust hero that plays a real MAISON DOUBA shipping video
 * silently, on loop, autoplay. Drops in a discreet gradient overlay + CTA to
 * /a-propos#envois. Falls back to a static poster if no video is uploaded yet.
 */
const TrustVideoHero = () => {
  const [video, setVideo] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchMedia({ category: "envois" })
      .then((items) => {
        if (!alive) return;
        // Prefer featured videos; fall back to any video
        const featuredVideo = items.find((m) => m.media_type === "video" && m.featured);
        const anyVideo = items.find((m) => m.media_type === "video");
        setVideo(featuredVideo || anyVideo || null);
      })
      .catch(() => setVideo(null));
    return () => { alive = false; };
  }, []);

  return (
    <section
      data-testid="trust-video-hero"
      className="relative bg-black border-y border-white/5 overflow-hidden"
    >
      <div className="grid md:grid-cols-2 items-stretch min-h-[70vh] md:min-h-[80vh]">
        {/* LEFT — video */}
        <div className="relative bg-[#0a0a0a] min-h-[50vh] md:min-h-0">
          {video ? (
            <>
              <video
                src={video.media_url}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                onLoadedData={() => setLoaded(true)}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${loaded ? "opacity-100" : "opacity-0"}`}
              />
              {!loaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full border border-[color:var(--gold)]/60 flex items-center justify-center backdrop-blur-sm bg-black/40">
                    <Play size={22} className="text-[color:var(--gold)] ml-0.5" strokeWidth={1.4} />
                  </div>
                </div>
              )}
              {/* Corner label — REC-style live indicator */}
              <div className="absolute top-6 left-6 flex items-center gap-3 label-caps text-white/80 text-[10px] backdrop-blur-sm bg-black/40 px-3 py-1.5 border border-white/10">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                Réel · Aujourd'hui
              </div>
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center max-w-sm px-6">
                <PackageCheck size={40} className="text-[color:var(--gold)]/60 mx-auto mb-6" strokeWidth={1.2} />
                <p className="label-caps text-white/40 text-[10px]">
                  Vidéo à venir · Uploadez depuis /admin/media
                </p>
              </div>
            </div>
          )}
          {/* Gradient overlay on right edge for smooth merge with text panel */}
          <div className="hidden md:block absolute top-0 right-0 bottom-0 w-32 bg-gradient-to-r from-transparent to-[#050505] pointer-events-none" />
          {/* Bottom fade for mobile stack readability */}
          <div className="md:hidden absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent pointer-events-none" />
        </div>

        {/* RIGHT — Copy */}
        <div className="relative flex items-center bg-[#050505]">
          <div className="w-full px-8 md:px-16 lg:px-20 py-16 md:py-0 max-w-2xl">
            <p className="label-caps text-[color:var(--gold)] mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-[color:var(--gold)]" />
              Preuves & transparence
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-white mb-8">
              Un vrai stock,<br />
              <em className="italic text-[color:var(--gold-soft)]">de vrais colis,</em><br />
              expédiés chaque jour.
            </h2>
            <p className="text-white/60 font-light text-base md:text-lg leading-relaxed mb-10 max-w-lg">
              MAISON DOUBA n'est pas un site vitrine. Notre atelier prépare et expédie chaque jour
              des dizaines de commandes vers nos clients TikTok Shop France, Italie et Espagne.
              Découvrez les preuves.
            </p>

            {/* Micro proofs */}
            <div className="grid grid-cols-3 gap-3 mb-10">
              {[
                { icon: PackageCheck, label: "+1000 colis/mois" },
                { icon: Truck, label: "48–72h envoi" },
                { icon: ShieldCheck, label: "100% authentique" },
              ].map((it) => (
                <div key={it.label} className="border border-white/10 p-3 flex items-center gap-2">
                  <it.icon size={14} className="text-[color:var(--gold)] shrink-0" strokeWidth={1.4} />
                  <span className="text-white/70 text-[11px] label-caps leading-tight">{it.label}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/a-propos#envois"
                data-testid="trust-hero-cta"
                className="btn-gold inline-flex items-center gap-3"
              >
                Voir nos preuves d'expédition
                <ArrowRight size={14} />
              </Link>
              <Link
                to="/a-propos"
                data-testid="trust-hero-cta-secondary"
                className="label-caps text-white/50 hover:text-[color:var(--gold)] text-[11px] transition-colors"
              >
                La Maison
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustVideoHero;
